import { useState, FormEvent } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subStatus, setSubStatus] = useState<"idle" | "loading" | "done">("idle");

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubStatus("loading");
    const data = new URLSearchParams({ email });
    try {
      await fetch("https://readdy.ai/api/form/d7cjbi4kpdh0a4advjrg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubStatus("done");
    } catch {
      setSubStatus("done");
    }
  };

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-gray-100 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://public.readdy.ai/ai/img_res/b92b84c7-3d6a-4e6c-9844-9c809a893419.png"
                alt="SilentNet Logo"
                className="h-8 w-auto object-contain"
              />
              <span className="font-bold text-lg text-gray-900">SilentNet</span>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              Professional IT services and support for businesses of all sizes. Remote and onsite — we&apos;ve got you covered.
            </p>
            <div className="flex gap-3">
              {["ri-linkedin-box-line", "ri-twitter-x-line", "ri-facebook-box-line"].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  rel="nofollow"
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-200 text-gray-500 hover:bg-teal-500 hover:text-white transition-colors cursor-pointer"
                >
                  <i className={icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-gray-900 text-sm mb-5">
              <a id="footer-services" href="#services">Services</a>
            </h4>
            <ul className="flex flex-col gap-3">
              {["Remote Support", "Onsite Services", "Cybersecurity", "Cloud Services", "Network Infrastructure", "Managed IT"].map((s) => (
                <li key={s}>
                  <button
                    onClick={() => handleNav("#services")}
                    className="text-gray-500 text-sm hover:text-teal-600 transition-colors cursor-pointer text-left"
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-bold text-gray-900 text-sm mb-5">Company</h4>
            <ul className="flex flex-col gap-3">
              {[
                { label: "About Us", href: "#about" },
                { label: "Testimonials", href: "#testimonials" },
                { label: "FAQ", href: "#faq" },
                { label: "Contact", href: "#contact" },
              ].map((item) => (
                <li key={item.label}>
                  <button
                    onClick={() => handleNav(item.href)}
                    className="text-gray-500 text-sm hover:text-teal-600 transition-colors cursor-pointer text-left"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-bold text-gray-900 text-sm mb-5">Stay Updated</h4>
            <p className="text-gray-500 text-sm mb-4">
              Get IT tips, security alerts, and company news delivered to your inbox.
            </p>
            {subStatus === "done" ? (
              <div className="flex items-center gap-2 text-teal-600 text-sm font-medium">
                <i className="ri-checkbox-circle-line text-lg" />
                You&apos;re subscribed!
              </div>
            ) : (
              <form
                id="newsletter-form"
                data-readdy-form
                onSubmit={handleSubscribe}
                className="flex flex-col gap-3"
              >
                <input
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-teal-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={subStatus === "loading"}
                  className="w-full py-2.5 rounded-lg bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {subStatus === "loading" ? "Subscribing..." : "Subscribe"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-xs">
            &copy; {new Date().getFullYear()} SilentNet. All rights reserved.
          </p>
          <div className="flex gap-6">
            {["Privacy Policy", "Terms of Service", "Cookie Policy"].map((item) => (
              <a
                key={item}
                href="#"
                rel="nofollow"
                className="text-gray-400 text-xs hover:text-gray-600 transition-colors cursor-pointer"
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
